import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../assets/styles/InvestorsPage.css';

interface InvestorProfile {
  id: number;
  name: string;
  logo: string;
  description: string;
  type: 'lead' | 'investor' | 'partner';
}

interface Milestone {
  id: number;
  date: string;
  title: string;
  description: string;
}

const InvestorsPage: React.FC = () => {
  // Sample investors data
  const investors: InvestorProfile[] = [
    {
      id: 1,
      name: "Munir Antum",
      logo: "/images/investor.jpg",
      description: "Lead investor in our Seed round, first customer, and strategic partner. Munir actively uses Price on Rails and has been a key advisor in its development.",
      type: "lead"
    },
    {
      id: 2,
      name: "Carlos Burza",
      logo: "/images/investor.jpg",
      description: "Carlos Burza is a strategic investor and advisor. He is a successful lawyer and entrepreneur with a passion for technology and finance.",
      type: "investor"
    },
    {
      id: 3,
      name: " ",
      logo: "/images/tradier.svg",
      description: "Tradier and its founder Dan Raju are strategic partners from our early days. Tradier is a leading brokerage API provider and has integrated with our platform.",
      type: "partner"
    },
    {
      id: 4,
      name: " ",
      logo: "/images/tradestation-logo.webp",
      description: "TradeStation is a strategic API partner. They have integrated with our platform and are helping us expand into new markets.",
      type: "partner"
    }
  ];

  // Sample milestones
  const milestones: Milestone[] = [
    {
      id: 1,
      date: "Q2 2018",
      title: "Company Founded",
      description: "Translate Stocks founded by Carlos Martins, and Juliana Rezende with a vision to transform trading through AI."
    },
    {
      id: 2,
      date: "Q3 2021",
      title: "Integrated with Major Brokers",
      description: "We achieved a huge milestone by integrating Tradier, TradeStation, and Interactive Brokers into our system."
    },
    {
      id: 3,
      date: "Q2 2022",
      title: "Beta Launch",
      description: "Released beta version of the Translate Stocks platform to a select group of traders for testing and feedback."
    },
    {
      id: 4,
      date: "Q1 2023",
      title: "Official Launch",
      description: "Publicly launched the Translate Stocks platform with support for equities and futures markets."
    },
    {
      id: 5,
      date: "Q2 2024",
      title: "The Price on Rails",
      description: "Initiated the development of Price on Rails technology, an AI trading system designed to predict market movements."
    },
    {
      id: 6,
      date: "Q4 2024",
      title: "Seed Funding",
      description: "Secured $100k in seed funding to launch the Price on Rails technology that was already developed."
    },
    {
      id: 7,
      date: "Q1 2025",
      title: "Agentic Bot Launch",
      description: "Introduced the Agentic Bot, expanding our platform's capabilities with automated trading and ML features."
    }
  ];

  // Financial highlights (simplified for this example)
  const financialHighlights = {
    marketSize: {
      value: "$3.5B",
      label: "North America Online Trading Platform Market (2023)"
    },
    cagr: {
      value: "6.5%",
      label: "Market CAGR (2024-2030)"
    },
    segment: {
      value: "#1",
      label: "Platform: Largest Revenue Component (2023)"
    },
    funding: {
      value: "$100K",
      label: "TranslateStocks.com Pre-Seed Funding (Dec 2024)"
    }
  };

  return (
    <>
      <Helmet>
        <title>Investor Relations | Translate Stocks</title>
        <meta name="description" content="Information for investors and potential partners about Translate Stocks' growth, milestones, and financial performance." />
        <meta name="keywords" content="translate stocks investors, AI trading investment, fintech investment opportunity" />
      </Helmet>
      
      <Header />
      
      <main className="investors-page">
        <section className="hero-section investors-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Investor <span className="highlight">Relations</span></h1>
              <p className="hero-subtitle">
                Translate Stocks is transforming the trading landscape with AI-powered technology. 
                Learn about our growth story, milestones, and investment opportunities.
              </p>
            </div>
          </div>
        </section>
        
        <section className="company-overview">
          <div className="container">
            <div className="overview-grid">
              <div className="overview-content">
                <h2>Company Overview</h2>
                <p>
                  Translate Stocks was founded in Q2 2018 by Carlos Martins and Juliana Rezende with a vision 
                  to transform trading through AI. In a North American online trading platform market now valued at 
                  $3.5 billion and growing at 6.5% annually, we're uniquely positioned with our flagship product, 
                  Price on Rails, which uses advanced AI to predict market movements and help traders make faster, 
                  more informed decisions.
                </p>
                <p>
                  After integrating with major brokers like Tradier, TradeStation, and Interactive Brokers in 2021, 
                  we launched our beta platform in Q2 2022, followed by our official launch in Q1 2023. We've focused 
                  on the platform segment—recognized as both the largest and fastest-growing component of the online 
                  trading market. Our proprietary technology combines deep learning, natural language processing, and 
                  time series analysis to provide predictive insights previously available only to institutional investors.
                </p>
                <p>
                  In 2024, we developed our innovative Price on Rails technology and secured $100k in pre-seed 
                  funding through SAFE agreements to accelerate its launch. As North America continues to lead the 
                  global online trading platform market (currently representing 36.6% of the global market), we're 
                  well-positioned for growth. Looking ahead to Q1 2025, we're introducing our Agentic Bot, expanding our 
                  platform's capabilities with automated trading and advanced machine learning features—innovations that 
                  will help us capitalize on the projected market expansion to $5.4 billion by 2030.
                </p>
                
                <a href="/about" className="btn btn-primary btn-large">
                  Learn More About Us
                </a>
                
              </div>
              
              <div className="overview-video">
                <div className="video-container">                
                  <iframe
                    src="https://player.vimeo.com/video/702914947?h=3556c044f&badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Start Screen - Today's Buy Signals - with VO + music"
                  ></iframe>
                </div>
                <p className="video-caption">Watch our investor overview video</p>
              </div>
            </div>
          </div>
        </section>
        
        <section className="financial-highlights">
          <div className="container">
            <h2 className="section-title">Financial Highlights</h2>
            
            <div className="highlights-grid">
              <div className="highlight-card">
                <span className="highlight-value">{financialHighlights.segment.value}</span>
                <span className="highlight-label">{financialHighlights.segment.label}</span>
                <a href="https://www.grandviewresearch.com/horizon/outlook/online-trading-platform-market/north-america" className="stat-source" target="_blank" rel="noopener noreferrer">*</a>
              </div>

              <div className="highlight-card">
                <span className="highlight-value">{financialHighlights.marketSize.value}</span>
                <span className="highlight-label">{financialHighlights.marketSize.label}</span>
                <a href="https://www.grandviewresearch.com/horizon/outlook/online-trading-platform-market/north-america" className="stat-source" target="_blank" rel="noopener noreferrer">*</a>
              </div>
              
              <div className="highlight-card">
                <span className="highlight-value">{financialHighlights.cagr.value}</span>
                <span className="highlight-label">{financialHighlights.cagr.label}</span>
                <a href="https://www.grandviewresearch.com/horizon/outlook/online-trading-platform-market/north-america" className="stat-source" target="_blank" rel="noopener noreferrer">*</a>
              </div>
              
              <div className="highlight-card">
                <span className="highlight-value">{financialHighlights.funding.value}</span>
                <span className="highlight-label">{financialHighlights.funding.label}</span>
              </div>
            </div>
            {/* 
            <div className="financials-cta">
              <a href="/investors/financial-reports" className="btn btn-outline">View Detailed Financial Reports</a>
            </div>
            */}
          </div>
        </section>
        
        <section className="growth-milestones">
          <div className="container">
            <h2 className="section-title">Growth Milestones</h2>
            
            <div className="timeline">
              {milestones.map((milestone, index) => (
                <div key={milestone.id} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <span className="timeline-date">{milestone.date}</span>
                    <h3 className="timeline-title">{milestone.title}</h3>
                    <p className="timeline-description">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <section className="investor-profiles">
          <div className="container">
            <h2 className="section-title">Our Investors & Partners</h2>
            
            <div className="investors-grid">
              {investors.map(investor => (
                <div key={investor.id} className={`investor-card ${investor.type}`}>
                  {investor.type === 'lead' && <span className="investor-badge">Lead Investor</span>}
                  {investor.type === 'partner' && <span className="investor-badge partner">Strategic Partner</span>}
                  <div className="investor-logo">
                    <img 
                      src={investor.logo} 
                      alt={investor.name} 
                      width="180" 
                      height="60"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="investor-name">{investor.name}</h3>
                  <p className="investor-description">{investor.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <section className="market-opportunity">
          <div className="container">
            <div className="opportunity-wrapper">
              <div className="opportunity-content">
                <h2>Market Opportunity</h2>
                <p>
                  The North American online trading platform market reached $3.5 billion in 2023 and is 
                  projected to grow to $5.4 billion by 2030, with a steady CAGR of 6.5%. North America 
                  currently dominates the global market, accounting for 36.6% of the worldwide online 
                  trading platform market.
                </p>
                <p>
                  Key market insights that align with our strategy:
                </p>
                <ul className="opportunity-list">
                  <li>The platform segment is both the largest and fastest-growing component of the market</li>
                  <li>North America is projected to maintain its leadership position through 2030</li>
                  <li>Increasing adoption of AI and machine learning technologies is driving market growth</li>
                  <li>Asia Pacific represents an expansion opportunity, projected to reach $3.8B by 2030</li>
                </ul>
                <p>
                  With our focus on the platform segment and our innovative AI technology, Translate Stocks 
                  is strategically positioned to capture significant market share in this growing industry. Our 
                  Price on Rails and upcoming Agentic Bot directly address the increasing demand for advanced 
                  trading technologies powered by artificial intelligence.
                </p>
              </div>
              
              <div className="opportunity-chart">
                <img 
                  src="/images/market-opportunity-chart.jpg" 
                  alt="Market Growth Projection" 
                  width="540" 
                  height="400"
                  loading="lazy"
                />
                <p className="chart-caption">North America Online Trading Platform Market Growth (2023-2030)<br />
                  <a href="https://www.grandviewresearch.com/horizon/outlook/online-trading-platform-market/north-america" className="source-link" target="_blank" rel="noopener noreferrer">Source: Grand View Research</a>
                </p>
              </div>
            </div>
          </div>
        </section>
        {/*
        <section className="investor-resources">
          <div className="container">
            <h2 className="section-title">Investor Resources</h2>
            
            <div className="resources-grid">
              <a href="/investors/presentations" className="resource-card">
          <div className="resource-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M40 12H36V8C36 6.9 35.1 6 34 6H14C12.9 6 12 6.9 12 8V12H8C6.9 12 6 12.9 6 14V40C6 41.1 6.9 42 8 42H40C41.1 42 42 41.1 42 40V14C42 12.9 41.1 12 40 12ZM16 8H32V12H16V8ZM40 40H8V14H40V40ZM32 22H24V30H32V22ZM22 16H16V18H22V16ZM22 20H16V22H22V20ZM22 24H16V26H22V24ZM22 28H16V30H22V28ZM22 32H16V34H22V32ZM32 32H24V34H32V32ZM32 16H24V18H32V16Z" fill="#2563EB"/>
            </svg>
          </div>
          <h3>Investor Presentations</h3>
          <p>Access our latest investor deck and presentation materials</p>
              </a>
              
              <a href="/investors/financial-reports" className="resource-card">
          <div className="resource-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M40 8H8C5.8 8 4 9.8 4 12V36C4 38.2 5.8 40 8 40H40C42.2 40 44 38.2 44 36V12C44 9.8 42.2 8 40 8ZM8 36V12H22V36H8ZM40 36H26V12H40V36ZM32 20H34V28H32V20ZM36 16H38V28H36V16ZM28 24H30V28H28V24Z" fill="#2563EB"/>
            </svg>
          </div>
          <h3>Financial Reports</h3>
          <p>Quarterly and annual financial statements and metrics</p>
              </a>
              
              <a href="/investors/news" className="resource-card">
          <div className="resource-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M40 8H8C5.8 8 4 9.8 4 12V36C4 38.2 5.8 40 8 40H40C42.2 40 44 38.2 44 36V12C44 9.8 42.2 8 40 8ZM40 36H8V12H40V36ZM36 16H12V20H36V16ZM36 24H12V28H36V24ZM30 32H12V36H30V32Z" fill="#2563EB"/>
            </svg>
          </div>
          <h3>Investor News</h3>
          <p>Press releases and updates relevant to investors</p>
              </a>
              
              <a href="/investors/governance" className="resource-card">
          <div className="resource-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 4L8 12V22.18C8 31.28 14.82 39.7 24 42C33.18 39.7 40 31.28 40 22.18V12L24 4ZM36 22.18C36 29.2 31.12 35.62 24 37.84C16.88 35.62 12 29.2 12 22.18V14.5L24 8.5L36 14.5V22.18ZM16.4 22.8L14 25.2L22 33.2L34 21.2L31.6 18.8L22 28.4L16.4 22.8Z" fill="#2563EB"/>
            </svg>
          </div>
          <h3>Governance</h3>
          <p>Board information, corporate governance, and regulatory filings</p>
              </a>
            </div>
          </div>
        </section>
        
        <section className="investor-contact">
          <div className="container">
            <div className="contact-wrapper">
              <h2>Investor Relations Contact</h2>
              <p>
          For investor inquiries or to schedule a meeting with our management team, 
          please contact our Investor Relations department.
              </p>
              
              <div className="contact-info">
          <div className="contact-person">
            <img 
              src="/images/investor-relations-contact.jpg" 
              alt="Head of Investor Relations" 
              width="120" 
              height="120"
              loading="lazy"
            />
            <div>
              <h3>Robert Garcia</h3>
              <p>Head of Investor Relations</p>
            </div>
          </div>
          
          <div className="contact-details">
            <div className="contact-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="#2563EB"/>
              </svg>
              <a href="mailto:investors@translatestocks.com">investors@translatestocks.com</a>
            </div>
            <div className="contact-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 15.5C18.8 15.5 17.5 15.3 16.4 14.9C16.3 14.9 16.2 14.9 16.1 14.9C15.8 14.9 15.6 15 15.4 15.2L13.2 17.4C10.4 15.9 8 13.6 6.6 10.8L8.8 8.6C9.1 8.3 9.2 7.9 9 7.6C8.7 6.5 8.5 5.2 8.5 4C8.5 3.5 8 3 7.5 3H4C3.5 3 3 3.5 3 4C3 13.4 10.6 21 20 21C20.5 21 21 20.5 21 20V16.5C21 16 20.5 15.5 20 15.5ZM19 12H21C21 7 17 3 12 3V5C15.9 5 19 8.1 19 12ZM15 12H17C17 9.2 14.8 7 12 7V9C13.7 9 15 10.3 15 12Z" fill="#2563EB"/>
              </svg>
              <a href="tel:+18005551003">+1 (800) 555-1003</a>
            </div>
          </div>
              </div>
              
              <div className="mailing-address">
          <h3>Mailing Address</h3>
          <address>
            Translate Stocks, Inc.<br />
            Investor Relations Department<br />
            123 Trading Street, Suite 500<br />
            New York, NY 10001
          </address>
              </div>
            </div>
          </div>
        </section>
        */}
      </main>
      
    </>
  );
};

export default InvestorsPage;